import { generatePath, matchPath } from "react-router-dom";

export type AppRouteParams = Record<string, string>;

type RouteLabelResolver = (params: AppRouteParams) => string;

export type AppPageRoute = {
  breadcrumbHref?: string;
  id: string;
  kind: "page";
  label: string;
  labelFromParams?: RouteLabelResolver;
  parentId?: string;
  path: string;
};

export type AppRedirectRoute = {
  id: string;
  kind: "redirect";
  path: string;
  pathname: string;
  setParams?: Record<string, string>;
};

export type AppCrumbRoute = {
  breadcrumbHref: string;
  id: string;
  kind: "crumb";
  label: string;
  parentId: string;
};

export type AppRoute = AppPageRoute | AppRedirectRoute | AppCrumbRoute;

export type MatchedAppRoute = {
  params: AppRouteParams;
  route: AppPageRoute | AppRedirectRoute;
};

const formatSegmentLabel = (segment: string) =>
  segment
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const page = (route: Omit<AppPageRoute, "kind">): AppPageRoute => ({
  kind: "page",
  ...route,
});

const redirect = (route: Omit<AppRedirectRoute, "kind">): AppRedirectRoute => ({
  kind: "redirect",
  ...route,
});

const crumb = (route: Omit<AppCrumbRoute, "kind">): AppCrumbRoute => ({
  kind: "crumb",
  ...route,
});

const catalogRoutes: AppRoute[] = [
  page({ id: "dashboard", path: "/dashboard", label: "Dashboard" }),
  page({ id: "sports", path: "/sports", label: "Sports", parentId: "dashboard" }),
  page({
    id: "sport-details",
    path: "/sports/:id",
    label: "Sport Details",
    parentId: "sports",
  }),
  page({ id: "teams", path: "/teams", label: "Teams", parentId: "dashboard" }),
  page({
    id: "team-details",
    path: "/teams/:id",
    label: "Team Details",
    parentId: "teams",
  }),
  page({ id: "athletes", path: "/athletes", label: "Athletes", parentId: "dashboard" }),
  page({
    id: "athlete-profile",
    path: "/athletes/:id",
    label: "Athlete Profile",
    parentId: "athletes",
  }),
  page({
    id: "merchandise",
    path: "/merchandise",
    label: "Merchandise",
    parentId: "dashboard",
  }),
  page({
    id: "merchandise-details",
    path: "/merchandise/:id",
    label: "Item Details",
    parentId: "merchandise",
  }),
  page({ id: "news", path: "/news", label: "News", parentId: "dashboard" }),
  page({
    id: "news-details",
    path: "/news/:id",
    label: "Article Details",
    parentId: "news",
  }),
];

const platformRoutes: AppRoute[] = [
  page({ id: "users", path: "/users", label: "Users", parentId: "dashboard" }),
  page({
    id: "user-profile",
    path: "/users/:id",
    label: "User Profile",
    parentId: "users",
  }),
  page({
    id: "countries",
    path: "/countries",
    label: "Countries",
    parentId: "dashboard",
  }),
  page({
    id: "administrative-regions",
    path: "/workspace/administrative-regions",
    label: "Regions",
    parentId: "dashboard",
  }),
  page({
    id: "sport-age-groups",
    path: "/workspace/sport-age-groups",
    label: "Age Groups",
    parentId: "dashboard",
  }),
];

const profileRoutes: AppRoute[] = [
  page({ id: "profile", path: "/profile", label: "Profile", parentId: "dashboard" }),
  page({
    id: "profile-edit",
    path: "/profile/edit",
    label: "Edit Profile",
    parentId: "profile",
  }),
];

const schoolOpsRoutes: AppRoute[] = [
  page({
    id: "school",
    path: "/workspace/school",
    label: "School",
    parentId: "dashboard",
  }),
  crumb({
    id: "school-staff",
    label: "Staff",
    parentId: "school",
    breadcrumbHref: "/workspace/school?tab=staff",
  }),
  page({
    id: "school-staff-profile",
    path: "/workspace/school-roles/users/:id",
    label: "Staff Profile",
    parentId: "school-staff",
  }),
  page({
    id: "school-team-profile",
    path: "/workspace/school-teams/:schoolId/:id",
    label: "Team Profile",
    parentId: "school-teams",
  }),
  page({
    id: "school-player-profile",
    path: "/workspace/school-players/:schoolId/:id",
    label: "Player Profile",
    parentId: "school-players",
  }),
  page({
    id: "school-roster-profile",
    path: "/workspace/school-rosters/:schoolId/:matchId/:id",
    label: "Roster Profile",
    parentId: "schedule",
  }),
  page({
    id: "school-scoresheet",
    path: "/workspace/school-scoresheets/:matchId",
    label: "Scoresheet",
    parentId: "schedule",
  }),
  page({
    id: "school-match",
    path: "/workspace/school-matches/:matchId",
    label: "Match",
    parentId: "schedule",
  }),
  page({
    id: "school-teams",
    path: "/workspace/school-teams",
    label: "Teams",
    parentId: "dashboard",
  }),
  page({
    id: "school-players",
    path: "/workspace/school-players",
    label: "People",
    parentId: "dashboard",
  }),
  page({
    id: "schedule",
    path: "/workspace/schedule",
    label: "Schedule",
    parentId: "dashboard",
  }),
  page({
    id: "player-performance-records",
    path: "/workspace/player-performance-records",
    label: "Performance & Highlights",
    parentId: "dashboard",
  }),
  redirect({
    id: "school-matches-list",
    path: "/workspace/school-matches",
    pathname: "/workspace/schedule",
    setParams: { view: "list" },
  }),
  redirect({
    id: "school-calendar",
    path: "/workspace/school-calendar",
    pathname: "/workspace/schedule",
    setParams: { view: "calendar" },
  }),
  redirect({
    id: "school-scoresheets-list",
    path: "/workspace/school-scoresheets",
    pathname: "/workspace/schedule",
    setParams: { view: "list", focus: "scoring" },
  }),
  redirect({
    id: "school-rosters-list",
    path: "/workspace/school-rosters",
    pathname: "/workspace/schedule",
    setParams: { view: "list" },
  }),
  redirect({
    id: "schools-alias",
    path: "/workspace/schools",
    pathname: "/workspace/school",
  }),
  redirect({
    id: "school-roles",
    path: "/workspace/school-roles",
    pathname: "/workspace/school",
    setParams: { tab: "staff" },
  }),
  redirect({
    id: "school-sports",
    path: "/workspace/school-sports",
    pathname: "/workspace/school",
    setParams: { tab: "programmes" },
  }),
];

const athleteToolRoutes: AppRoute[] = [
  page({
    id: "workspace-events",
    path: "/workspace/events",
    label: "Events",
    parentId: "dashboard",
  }),
  page({
    id: "workspace-player-classes",
    path: "/workspace/player-classes",
    label: "Classes",
    parentId: "dashboard",
  }),
  page({
    id: "workspace-requests",
    path: "/workspace/requests",
    label: "Requests",
    parentId: "dashboard",
  }),
  page({
    id: "workspace-player-profile",
    path: "/workspace/player-profile",
    label: "My Athlete Profile",
    parentId: "dashboard",
  }),
  page({
    id: "workspace-player-stats",
    path: "/workspace/player-stats",
    label: "My Stats",
    parentId: "dashboard",
  }),
];

const commerceRoutes: AppRoute[] = [
  page({
    id: "workspace-orders",
    path: "/workspace/orders",
    label: "Orders",
    parentId: "dashboard",
  }),
  page({
    id: "workspace-payments",
    path: "/workspace/payments",
    label: "Payments",
    parentId: "dashboard",
  }),
  page({
    id: "workspace-wallet",
    path: "/workspace/wallet",
    label: "Wallet",
    parentId: "dashboard",
  }),
];

const communityRoutes: AppRoute[] = [
  page({
    id: "workspace-notifications",
    path: "/workspace/notifications",
    label: "Notifications",
    parentId: "dashboard",
  }),
  page({
    id: "workspace-followed-athletes",
    path: "/workspace/followed-athletes",
    label: "Followed Athletes",
    parentId: "dashboard",
  }),
];

const workspaceCatchAll: AppPageRoute = page({
  id: "workspace-section",
  path: "/workspace/:section",
  label: "Workspace",
  parentId: "dashboard",
  labelFromParams: (params) => formatSegmentLabel(params.section || "Workspace"),
});

export const APP_ROUTES: AppRoute[] = [
  ...catalogRoutes,
  ...platformRoutes,
  ...profileRoutes,
  ...schoolOpsRoutes,
  ...athleteToolRoutes,
  ...commerceRoutes,
  ...communityRoutes,
  workspaceCatchAll,
];

export const APP_ROUTER_ROUTES = APP_ROUTES.filter(
  (route): route is AppPageRoute | AppRedirectRoute => route.kind !== "crumb",
);

export const APP_ROUTES_BY_ID: Record<string, AppRoute> = APP_ROUTES.reduce((routes, route) => {
  routes[route.id] = route;
  return routes;
}, {} as Record<string, AppRoute>);

const routeSpecificity = (path: string) =>
  path
    .split("/")
    .filter(Boolean)
    .reduce((score, part) => score + (part.startsWith(":") ? 1 : 10), 0);

const toRouteParams = (params: Record<string, string | undefined> | undefined): AppRouteParams => {
  if (!params) {
    return {};
  }

  return Object.entries(params).reduce((resolved, [key, value]) => {
    if (value) {
      resolved[key] = value;
    }
    return resolved;
  }, {} as AppRouteParams);
};

export const matchAppRoute = (pathname: string): MatchedAppRoute | null => {
  let best: MatchedAppRoute | null = null;
  let bestScore = -1;

  APP_ROUTER_ROUTES.forEach((route) => {
    const match = matchPath(pathname, { exact: true, path: route.path });

    if (!match) {
      return;
    }

    const score = routeSpecificity(route.path);

    if (score > bestScore) {
      bestScore = score;
      best = {
        params: toRouteParams(match.params),
        route,
      };
    }
  });

  return best;
};

export const resolveRouteHref = (
  route: AppPageRoute | AppCrumbRoute,
  params: AppRouteParams,
): string => {
  if (route.kind === "crumb") {
    return route.breadcrumbHref;
  }

  if (route.breadcrumbHref) {
    return route.breadcrumbHref;
  }

  try {
    return generatePath(route.path, params);
  } catch {
    return route.path;
  }
};

export const resolveRouteLabel = (route: AppPageRoute | AppCrumbRoute, params: AppRouteParams) => {
  if (route.kind === "page" && route.labelFromParams) {
    return route.labelFromParams(params);
  }

  return route.label;
};

export const formatPathSegmentLabel = formatSegmentLabel;
